export type ShareState = "active" | "revoked" | "expired" | "used";

export interface Share {
  id: string;
  key: string;
  oneTime: boolean;
  isFolder: boolean;
  createdAt: number;
  expiresAt: number | null;
  revoked: boolean;
  used: boolean;
  usedAt: number | null;
  downloads: number;
  state: ShareState;
  url?: string;
}

async function parseError(res: Response) {
  try {
    const body = (await res.json()) as { error?: string };
    return new Error(body?.error ?? res.statusText);
  } catch {
    return new Error(res.statusText || "Request failed");
  }
}

export async function listShares(): Promise<Share[]> {
  const res = await fetch("/api/shares", {
    headers: { "X-FlareDrive-Client": "web" },
  });
  if (!res.ok) throw await parseError(res);
  const { shares } = (await res.json()) as { shares: Share[] };
  return shares;
}

export async function createShare(options: {
  key: string;
  oneTime: boolean;
  expiresIn: number | null;
}): Promise<Share> {
  const res = await fetch("/api/shares", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-FlareDrive-Client": "web",
    },
    body: JSON.stringify(options),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as Share;
}

/** Turn a link off. Keeps the record visible unless `purge` is set. */
export async function revokeShare(id: string, purge = false): Promise<void> {
  const res = await fetch(`/api/shares/${id}${purge ? "?purge=1" : ""}`, {
    method: "DELETE",
    headers: { "X-FlareDrive-Client": "web" },
  });
  if (!res.ok) throw await parseError(res);
}

export function shareUrl(id: string) {
  return new URL(`/s/${id}`, window.location.href).toString();
}

/** Expiry choices offered in the share dialog, in seconds. */
export const EXPIRY_OPTIONS: { label: string; value: number | null }[] = [
  { label: "1 hour", value: 60 * 60 },
  { label: "24 hours", value: 24 * 60 * 60 },
  { label: "7 days", value: 7 * 24 * 60 * 60 },
  { label: "30 days", value: 30 * 24 * 60 * 60 },
  { label: "Never (revoke by hand)", value: null },
];

export function formatExpiry(share: Share) {
  if (share.state === "revoked") return "Turned off";
  if (share.state === "used") return "Used";
  if (share.state === "expired") return "Expired";
  if (share.expiresAt === null) return "No expiry";

  const remaining = share.expiresAt - Date.now();
  if (remaining <= 0) return "Expired";
  const hours = Math.floor(remaining / (60 * 60 * 1000));
  if (hours < 1) return `${Math.max(1, Math.round(remaining / 60000))} min left`;
  if (hours < 48) return `${hours}h left`;
  return `${Math.floor(hours / 24)}d left`;
}

/** Clipboard API needs a secure context; fall back for plain http. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

/** Turn off several links at once, reporting how many succeeded. */
export async function revokeMany(ids: string[], purge = false) {
  const results = await Promise.all(
    ids.map((id) =>
      revokeShare(id, purge).then(
        () => true,
        () => false
      )
    )
  );
  return {
    ok: results.filter(Boolean).length,
    failed: results.filter((r) => !r).length,
  };
}
